// src/pages/History.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { getMessages, getConnections } from '../api';

export default function History() {
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [connections, setConnections] = useState([]);

  // Fetch connections for dropdown
  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const res = await getConnections();
        if (res.status === 1) setConnections(res.data || []);
      } catch (e) {
        console.error('Failed to fetch connections', e);
      }
    };
    fetchConnections();
  }, []);

  // Fetch messages using the new API (getMessages)
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMessages(
        pagination.page,
        pagination.limit,
        activeSearch,
        selectedStatus,
        selectedConnectionId === 'all' ? null : selectedConnectionId
      );
      if (res.status === 1) {
        setMessages(res.data || []);
        setPagination(
          res.pagination || { page: 1, limit: 10, total: 0, totalPages: 1 }
        );
      }
    } catch (e) {
      console.error('Failed to fetch messages', e);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, activeSearch, selectedStatus, selectedConnectionId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Reset page when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [activeSearch, selectedStatus, selectedConnectionId]);

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setActiveSearch(searchTerm);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  const statusColors = {
    sent: '#22c55e',
    delivered: '#3b82f6',
    read: '#8b5cf6',
    failed: '#ef4444',
    pending: '#f59e0b',
    received: '#0b26f5',
  };

  const statusIcons = {
    sent: '✅',
    delivered: '📬',
    read: '👁️',
    failed: '❌',
    pending: '⏳',
    received: '📧',
  };

  const getStatusReason = (msg) => {
    if (msg.status === 'failed' && msg.error_message) return msg.error_message;
    return '—';
  };

  const getSchedule = (msg) => {
    if (msg.scheduled_at) return new Date(msg.scheduled_at).toLocaleString();
    return 'Non-Schedule';
  };

  // Get device display label (name + phone)
  const getDeviceLabel = (device) => {
    if (!device) return '—';
    let label = device.name || 'Unnamed';
    if (device.phone_number) label += ` (${device.phone_number})`;
    return label;
  };

  return (
    <div className="history-page full-width-page">
      {/* Header */}
      <div className="history-header">
        <div className="history-header-left">
          <h2>📋 Message List</h2>
          <span className="message-count">
            {pagination.total > 0 ? `${pagination.total} messages` : 'No messages'}
          </span>
        </div>
        {loading && <div className="loading-spinner">⟳</div>}
      </div>

      {/* Filters */}
      <div className="history-filters">
        <div className="filter-group">
          <label className="filter-label">Device</label>
          <select
            value={selectedConnectionId}
            onChange={(e) => setSelectedConnectionId(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Devices</option>
            {connections.map((conn) => (
              <option key={conn.id} value={conn.id}>
                {conn.name || conn.phone_number || 'Unnamed'}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Status</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="sent">✅ Sent</option>
            <option value="delivered">📬 Delivered</option>
            <option value="read">👁️ Read</option>
            <option value="failed">❌ Failed</option>
            <option value="pending">⏳ Pending</option>
          </select>
        </div>

        <div className="filter-group search-group">
          <label className="filter-label">Search</label>
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder="Search messages or numbers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyPress}
              className="search-input"
            />
            <button type="submit" className="search-btn">
              🔍
            </button>
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="history-table-wrapper">
        <table className="history-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Date & Time</th>
              <th>Mobile No.</th>
              <th>Message</th>
              <th>Device</th>
              <th>Status</th>
              <th>Schedule</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {messages.map((m, idx) => (
              <tr key={m.id} className={m.status === 'failed' ? 'row-failed' : ''}>
                <td className="col-index">
                  {(pagination.page - 1) * pagination.limit + idx + 1}
                </td>
                <td className="col-date">
                  {new Date(m.created_at).toLocaleString()}
                </td>
                <td className="col-number" style={{ maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {m.is_incoming ? (
                    <>
                      <span style={{ color: "#ef4444", fontWeight: 600 }}>From:</span>{" "}
                      {m.sender_name && m.sender_name !== m.recipient ? (
                        <>
                          {m.sender_name} ({m.recipient})
                        </>
                      ) : (
                        m.sender_name || m.recipient
                      )}
                    </>
                  ) : (
                    <>
                      <span style={{ color: "#22c55e", fontWeight: 600 }}>To:</span>{" "}
                      {m.recipient}
                    </>
                  )}
                </td>
                <td className="col-message">
                  <span className="message-preview">
                    {m.content?.substring(0, 45) || '—'}
                    {m.content?.length > 45 && '…'}
                  </span>
                </td>
                <td className="col-device">
                  {m.device ? (
                    <span className="device-badge">
                      {getDeviceLabel(m.device)}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="col-status">
                  <span
                    className="status-pill"
                    style={{ background: statusColors[m.status] || '#94a3b8' }}
                  >
                    {statusIcons[m.status] || '•'} {m.status}
                  </span>
                </td>
                <td className="col-schedule">
                  {getSchedule(m)}
                </td>
                <td className="col-actions">
                  <button className="action-btn view-btn" title="View details">
                    👁️
                  </button>
                  <button className="action-btn resend-btn" title="Resend">
                    ↻
                  </button>
                </td>
              </tr>
            ))}
            {messages.length === 0 && !loading && (
              <tr>
                <td colSpan="8" className="empty-state">
                  <span className="empty-icon">📭</span>
                  <p>No messages found</p>
                  <span className="empty-sub">Try adjusting your filters or search</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => goToPage(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="page-btn"
          >
            ‹ Previous
          </button>
          <div className="page-info">
            <span className="page-current">{pagination.page}</span>
            <span className="page-total">of {pagination.totalPages}</span>
          </div>
          <button
            onClick={() => goToPage(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className="page-btn"
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}